// =====================================================================
//  ADMIN CONSOLE, DATA TABLE
// ---------------------------------------------------------------------
//  One grid component behind every list in the console. It owns the
//  behaviour an administrator expects from a real product and that the
//  old hand-written <table> markup never had: sortable sticky headers,
//  row selection with a bulk-action bar, column visibility, pagination,
//  keyboard-navigable rows, and honest empty / loading / error states.
//
//  It works in two modes:
//
//    Client mode (default)  Pass the full `rows` array. Sorting, paging
//                           and the record count are handled here.
//
//    Server mode            Pass `total` (and control `sort`/`page` from
//                           the parent). The table renders exactly the
//                           rows it is given and delegates ordering and
//                           paging to the API, which is what the new
//                           `?search=&sort=&limit=` params are for.
//
//  COLUMN SHAPE
//    key        string   Row property, and the `?sort=` value in server mode.
//    label      node     Header text.
//    width      number   Fixed px width; omit to let the column flex.
//    align      'right'  Right-aligns and applies tabular numerals.
//    sortable   boolean  Defaults to true.
//    render     (row) => node
//    csv        (row) => string   Export value when `render` returns JSX.
//    sortValue  (row) => any      Overrides the value used for sorting.
//    defaultHidden boolean        Starts hidden but toggleable.
// =====================================================================

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { ArrowDown, ArrowUp, ChevronsUpDown, Columns3, Inbox, X } from 'lucide-react';
import {
  Button,
  Checkbox,
  EmptyState,
  Menu,
  MenuLabel,
  Pager,
  SkeletonRows,
  cx,
} from './ui';
import { compareValues } from './format';

const SELECT_COL_WIDTH = 34;

export default function DataTable({
  columns = [],
  rows = [],
  loading = false,
  error = null,

  getRowId = (row) => row?.id,
  onRowClick,
  rowActions,
  rowClassName,

  // selection
  selectable = false,
  selected = [],
  onSelectedChange,
  bulkActions,

  // sorting, omit to let the table manage it
  sort: sortProp,
  onSortChange,
  defaultSort,

  // pagination
  paginate = true,
  total: totalProp,
  page: pageProp,
  pageSize: pageSizeProp,
  onPageChange,
  onPageSizeChange,
  defaultPageSize = 25,

  // chrome
  toolbar,
  empty,
  emptyIcon = Inbox,
  emptyTitle = 'No records found',
  emptyText,
  showColumnToggle = true,
  maxBodyHeight,
  className = '',
  footer,
}) {
  const serverMode = totalProp !== undefined && totalProp !== null;

  /* ------------------------------ sorting ----------------------------- */
  const [sortState, setSortState] = useState(defaultSort || null);
  const sort = sortProp !== undefined ? sortProp : sortState;

  const applySort = useCallback(
    (key) => {
      const next =
        sort?.key === key
          ? sort.dir === 'asc'
            ? { key, dir: 'desc' }
            : null // third click clears back to the natural order
          : { key, dir: 'asc' };
      if (onSortChange) onSortChange(next);
      else setSortState(next);
    },
    [sort, onSortChange]
  );

  /* ---------------------------- pagination ---------------------------- */
  const [pageState, setPageState] = useState(0);
  const [pageSizeState, setPageSizeState] = useState(defaultPageSize);
  const page = pageProp !== undefined ? pageProp : pageState;
  const pageSize = pageSizeProp !== undefined ? pageSizeProp : pageSizeState;

  const setPage = useCallback(
    (next) => (onPageChange ? onPageChange(next) : setPageState(next)),
    [onPageChange]
  );

  const setPageSize = useCallback(
    (next) => {
      if (onPageSizeChange) onPageSizeChange(next);
      else setPageSizeState(next);
      setPage(0);
    },
    [onPageSizeChange, setPage]
  );

  /* ------------------------- column visibility ------------------------ */
  const [hidden, setHidden] = useState(() =>
    columns.filter((c) => c.defaultHidden).map((c) => c.key)
  );

  const visibleColumns = useMemo(
    () => columns.filter((c) => c && !hidden.includes(c.key)),
    [columns, hidden]
  );

  const toggleColumn = useCallback((key) => {
    setHidden((prev) => (prev.includes(key) ? prev.filter((k) => k !== key) : [...prev, key]));
  }, []);

  /* --------------------------- derived rows --------------------------- */
  const sortedRows = useMemo(() => {
    if (serverMode || !sort?.key) return rows;
    const column = columns.find((c) => c.key === sort.key);
    const valueOf = column?.sortValue || ((row) => row?.[sort.key]);
    // Copy before sorting: mutating the query cache's array in place makes
    // React Query hand back already-sorted data on the next render.
    return [...rows].sort((a, b) => compareValues(valueOf(a), valueOf(b), sort.dir));
  }, [rows, sort, columns, serverMode]);

  const total = serverMode ? totalProp : sortedRows.length;

  const pagedRows = useMemo(() => {
    if (serverMode || !paginate) return sortedRows;
    const start = page * pageSize;
    return sortedRows.slice(start, start + pageSize);
  }, [sortedRows, page, pageSize, paginate, serverMode]);

  // A filter change can leave us stranded past the last page.
  useEffect(() => {
    const pageCount = Math.max(1, Math.ceil(total / pageSize));
    if (page > pageCount - 1) setPage(0);
  }, [total, pageSize, page, setPage]);

  /* ---------------------------- selection ----------------------------- */
  const selectedSet = useMemo(() => new Set(selected.map(String)), [selected]);
  const pageIds = useMemo(() => pagedRows.map((row) => String(getRowId(row))), [pagedRows, getRowId]);
  const allOnPageSelected = pageIds.length > 0 && pageIds.every((id) => selectedSet.has(id));
  const someOnPageSelected = pageIds.some((id) => selectedSet.has(id)) && !allOnPageSelected;

  const toggleAllOnPage = useCallback(() => {
    if (!onSelectedChange) return;
    if (allOnPageSelected) {
      onSelectedChange(selected.filter((id) => !pageIds.includes(String(id))));
    } else {
      const merged = new Set(selected.map(String));
      pageIds.forEach((id) => merged.add(id));
      onSelectedChange(Array.from(merged));
    }
  }, [allOnPageSelected, onSelectedChange, pageIds, selected]);

  const toggleRow = useCallback(
    (id) => {
      if (!onSelectedChange) return;
      const key = String(id);
      onSelectedChange(
        selectedSet.has(key) ? selected.filter((s) => String(s) !== key) : [...selected, key]
      );
    },
    [onSelectedChange, selected, selectedSet]
  );

  const clearSelection = useCallback(() => onSelectedChange?.([]), [onSelectedChange]);

  /* ------------------------------ render ------------------------------ */
  const colSpan = visibleColumns.length + (selectable ? 1 : 0) + (rowActions ? 1 : 0);
  const hasRows = pagedRows.length > 0;
  const showBulkBar = selectable && selected.length > 0;

  const bodyStyle = maxBodyHeight ? { maxHeight: maxBodyHeight, overflowY: 'auto' } : undefined;

  return (
    <div className={cx('ac-panel', className)}>
      {toolbar}

      {showBulkBar ? (
        <div className="ac-bulkbar">
          <Checkbox checked indeterminate={false} onChange={clearSelection} aria-label="Clear selection" />
          <span className="ac-bulkbar-count">{selected.length} selected</span>
          <div style={{ flex: '1 1 auto' }} />
          {typeof bulkActions === 'function' ? bulkActions(selected, clearSelection) : bulkActions}
          <Button size="sm" variant="ghost" icon={X} onClick={clearSelection} aria-label="Clear selection" />
        </div>
      ) : null}

      <div className="ac-tablewrap" style={bodyStyle}>
        <table className="ac-table">
          <colgroup>
            {selectable ? <col style={{ width: SELECT_COL_WIDTH }} /> : null}
            {visibleColumns.map((column) => (
              <col key={column.key} style={column.width ? { width: column.width } : undefined} />
            ))}
            {rowActions ? <col style={{ width: 78 }} /> : null}
          </colgroup>

          <thead>
            <tr>
              {selectable ? (
                <th className="ac-td--tight" style={{ width: SELECT_COL_WIDTH }}>
                  <Checkbox
                    checked={allOnPageSelected}
                    indeterminate={someOnPageSelected}
                    onChange={toggleAllOnPage}
                    aria-label="Select all rows on this page"
                  />
                </th>
              ) : null}

              {visibleColumns.map((column) => {
                const sortable = column.sortable !== false;
                const isSorted = sort?.key === column.key;
                const SortIcon = !isSorted ? ChevronsUpDown : sort.dir === 'asc' ? ArrowUp : ArrowDown;
                return (
                  <th
                    key={column.key}
                    className={cx(
                      sortable && 'ac-th--sortable',
                      isSorted && 'ac-th--sorted',
                      column.align === 'right' && 'ac-th--num'
                    )}
                    onClick={sortable ? () => applySort(column.key) : undefined}
                    aria-sort={isSorted ? (sort.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
                    scope="col"
                  >
                    <span className="ac-th-inner">
                      {column.label}
                      {sortable ? <SortIcon size={11} strokeWidth={2.2} className="ac-sort-icon" /> : null}
                    </span>
                  </th>
                );
              })}

              {rowActions ? (
                <th style={{ width: 78, textAlign: 'right' }}>
                  {showColumnToggle ? (
                    <Menu
                      align="right"
                      width={200}
                      trigger={({ toggle }) => (
                        <button
                          type="button"
                          className="ac-iconbtn"
                          onClick={toggle}
                          aria-label="Choose columns"
                          title="Choose columns"
                          style={{ width: 24, height: 24 }}
                        >
                          <Columns3 size={13} strokeWidth={1.9} />
                        </button>
                      )}
                    >
                      <MenuLabel>Visible columns</MenuLabel>
                      {columns.map((column) => (
                        <label
                          key={column.key}
                          className="ac-menu-item"
                          style={{ cursor: 'pointer' }}
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            checked={!hidden.includes(column.key)}
                            onChange={() => toggleColumn(column.key)}
                          />
                          <span>{typeof column.label === 'string' ? column.label : column.key}</span>
                        </label>
                      ))}
                    </Menu>
                  ) : (
                    <span className="ac-sr">Actions</span>
                  )}
                </th>
              ) : null}
            </tr>
          </thead>

          {loading && !hasRows ? (
            <SkeletonRows rows={7} cols={colSpan} />
          ) : (
            <tbody>
              {error ? (
                <tr>
                  <td colSpan={colSpan} style={{ height: 'auto', padding: 0 }}>
                    <EmptyState
                      icon={X}
                      title="Could not load this data"
                      text={typeof error === 'string' ? error : error?.message || 'Please try again.'}
                    />
                  </td>
                </tr>
              ) : !hasRows ? (
                <tr>
                  <td colSpan={colSpan} style={{ height: 'auto', padding: 0 }}>
                    {empty || <EmptyState icon={emptyIcon} title={emptyTitle} text={emptyText} />}
                  </td>
                </tr>
              ) : (
                pagedRows.map((row) => {
                  const id = getRowId(row);
                  const isSelected = selectedSet.has(String(id));
                  return (
                    <tr
                      key={id ?? JSON.stringify(row).slice(0, 40)}
                      className={cx(
                        onRowClick && 'ac-tr--clickable',
                        isSelected && 'ac-tr--selected',
                        rowClassName?.(row)
                      )}
                      onClick={onRowClick ? () => onRowClick(row) : undefined}
                      tabIndex={onRowClick ? 0 : undefined}
                      onKeyDown={
                        onRowClick
                          ? (event) => {
                              if (event.key === 'Enter' || event.key === ' ') {
                                event.preventDefault();
                                onRowClick(row);
                              }
                            }
                          : undefined
                      }
                    >
                      {selectable ? (
                        <td
                          className="ac-td--tight"
                          onClick={(event) => event.stopPropagation()}
                        >
                          <Checkbox
                            checked={isSelected}
                            onChange={() => toggleRow(id)}
                            aria-label={`Select row ${id}`}
                          />
                        </td>
                      ) : null}

                      {visibleColumns.map((column) => (
                        <td
                          key={column.key}
                          className={cx(column.align === 'right' && 'ac-td--num', column.cellClassName)}
                        >
                          {column.render ? column.render(row) : formatCell(row?.[column.key])}
                        </td>
                      ))}

                      {rowActions ? (
                        <td className="ac-td--tight" onClick={(event) => event.stopPropagation()}>
                          <div className="ac-rowactions">{rowActions(row)}</div>
                        </td>
                      ) : null}
                    </tr>
                  );
                })
              )}
            </tbody>
          )}
        </table>
      </div>

      {footer}

      {paginate && (hasRows || total > 0) ? (
        <Pager
          page={page}
          pageSize={pageSize}
          total={total}
          onPageChange={setPage}
          onPageSizeChange={setPageSize}
        />
      ) : null}
    </div>
  );
}

/** Default cell rendering: never print "null" or "undefined" into a table. */
function formatCell(value) {
  if (value === null || value === undefined || value === '') {
    return <span style={{ color: 'var(--ac-faint)' }}>,</span>;
  }
  if (typeof value === 'boolean') return value ? 'Yes' : 'No';
  if (typeof value === 'object') return String(value);
  return value;
}

/**
 * Resolves a column set into the flat rows a CSV export needs, honouring
 * each column's `csv` accessor so exported values match what is on screen
 * even when the cell renders JSX.
 */
export function columnsToCsv(columns, rows) {
  const cols = columns
    .filter((c) => c && c.key !== '__actions')
    .map((c) => ({
      key: c.key,
      label: typeof c.label === 'string' ? c.label : c.key,
      csv: c.csv || ((row) => row?.[c.key]),
    }));
  return { columns: cols, rows };
}
